import { useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Slider, Text } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

export const getTemplateBundleDefaultModelPresetsQuery = graphql(`
  query getTemplateBundleDefaultModelPresets {
    templateBundleDefaultModels {
      __typename
      id
      name
      description
      rank
      modelArgsId
    }
  }
`);

export type ModelPreset = {
  id: string;
  name: string;
  description: string | null;
  modelArgsId: string;
};

export interface ModelPresetSliderProps {
  /** Notified whenever the selected preset changes (including the initial default) */
  onPresetSelected?: (preset: ModelPreset) => void;
  /**
   * Preselects the preset backed by this llm_model_args id (e.g. the
   * model an existing template was saved with). Falls back to the first
   * preset when no match exists.
   */
  initialModelArgsId?: string;
  /** Locks the control (read-only template view) */
  disabled?: boolean;
}

/**
 * Discrete slider over the configured template bundle default model
 * presets, ordered by rank (e.g. Fast → Thinking). The preset name is
 * the visible label; the selected preset's description is shown below
 * the control. Renders nothing when no presets are configured.
 */
export const ModelPresetSlider: React.FC<ModelPresetSliderProps> = ({
  onPresetSelected,
  initialModelArgsId,
  disabled = false,
}) => {
  const { data } = useQuery(getTemplateBundleDefaultModelPresetsQuery);

  const presets = useMemo<ModelPreset[]>(() => {
    return [...(data?.templateBundleDefaultModels ?? [])]
      .sort((a, b) => a.rank - b.rank)
      .map(({ id, name, description, modelArgsId }) => ({
        id,
        name,
        description,
        modelArgsId,
      }));
  }, [data]);

  const [selectedIndex, setSelectedIndex] = useState(0);

  // Pick the initial selection once presets load (the preset matching
  // initialModelArgsId, else the first) and notify the parent so it
  // knows a preset is in effect before the user touches the slider
  const hasNotifiedInitial = useRef(false);
  useEffect(() => {
    if (hasNotifiedInitial.current || presets.length === 0) {
      return;
    }
    hasNotifiedInitial.current = true;
    const matchIndex = initialModelArgsId
      ? presets.findIndex((p) => p.modelArgsId === initialModelArgsId)
      : -1;
    const index = matchIndex >= 0 ? matchIndex : 0;
    setSelectedIndex(index);
    onPresetSelected?.(presets[index]!);
  }, [presets, initialModelArgsId, onPresetSelected]);

  if (presets.length === 0) {
    return null;
  }

  const selectPreset = (index: number) => {
    setSelectedIndex(index);
    const preset = presets[index];
    if (preset) {
      onPresetSelected?.(preset);
    }
  };

  const selectedPreset = presets[Math.min(selectedIndex, presets.length - 1)];

  return (
    <div className="flex flex-col gap-2">
      <Text
        inline
        size="xs"
        weight="medium"
        className="tracking-wide text-text-tertiary uppercase"
      >
        Model
      </Text>
      <div className="mx-auto flex w-full max-w-xs flex-col gap-2 px-6">
        <Slider
          value={[Math.min(selectedIndex, presets.length - 1)]}
          onValueChange={([value]) => selectPreset(value ?? 0)}
          min={0}
          max={presets.length - 1}
          step={1}
          disabled={disabled || presets.length === 1}
          aria-label="Model"
        />
        {/* Each label is centered directly under its slider stop. The row is
            inset by half the thumb width (6px) to match how Radix keeps the
            thumb inside the track at the extremes. */}
        <div className="relative mx-1.5 h-4">
          {presets.map((preset, index) => {
            const stopPercent =
              presets.length === 1 ? 50 : (index / (presets.length - 1)) * 100;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => selectPreset(index)}
                disabled={disabled}
                style={{ left: `${stopPercent}%` }}
                className={`absolute -translate-x-1/2 rounded-sm whitespace-nowrap transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none ${
                  index === selectedIndex
                    ? 'text-text-primary'
                    : 'text-text-tertiary hover:text-text-secondary'
                }`}
              >
                <Text
                  inline
                  size="xs"
                  weight={index === selectedIndex ? 'semibold' : 'regular'}
                >
                  {preset.name}
                </Text>
              </button>
            );
          })}
        </div>
      </div>
      {/* Always-visible description (hover states are awkward on tablets),
          spanning the full width as its own section. All descriptions share
          one grid cell so the block reserves the height of the tallest one
          and the layout below never shifts. */}
      <div className="mt-4 grid">
        {presets.map((preset) => (
          <Text
            key={preset.id}
            size="xs"
            color="tertiary"
            className={`col-start-1 row-start-1 ${
              preset.id === selectedPreset?.id ? '' : 'invisible'
            }`}
          >
            {preset.description ?? ''}
          </Text>
        ))}
      </div>
    </div>
  );
};
